import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import {
  postMultipleKambiSharesToBetLoop,
  postMultipleSharesToBetLoop,
} from '@core/Areas/BetLoop/helpers/api'
import { handleShareToBetLoopMutateResponse } from '../helpers/handleShareToBetLoopMutateResponse'
import { trackShareBetError } from '@core/Areas/ShareBet/helpers/analytics'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import type {
  KambiTicketsToShare,
  SelectedKambiTickets,
  SelectedTickets,
  TicketsToShare,
} from '@core/Areas/BetLoop/Components/QuickShare/types'

export const usePostMultipleSharesToBetLoopMutation = ({
  ticketsToShare,
  kambiTicketsToShare,
  onDismiss,
  selectedTicketsRef,
  selectedKambiTicketsRef,
  setIsFirstShareModalOpen,
}: {
  ticketsToShare: TicketsToShare
  kambiTicketsToShare: KambiTicketsToShare
  selectedTicketsRef: React.MutableRefObject<SelectedTickets>
  selectedKambiTicketsRef: React.MutableRefObject<SelectedKambiTickets>
  onDismiss: () => void
  setIsFirstShareModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const [racingResponse, kambiResponse] = await Promise.all([
        ticketsToShare.size
          ? postMultipleSharesToBetLoop({
              ticketSequences: Array.from(ticketsToShare),
            })
          : Promise.resolve(undefined),

        kambiTicketsToShare.size
          ? postMultipleKambiSharesToBetLoop({
              bets: Array.from(kambiTicketsToShare),
            })
          : Promise.resolve(undefined),
      ])

      return { racingResponse, kambiResponse }
    },
    onSuccess: ({ racingResponse, kambiResponse }) => {
      handleShareToBetLoopMutateResponse({
        racingResponse,
        kambiResponse,
        selectedTickets: selectedTicketsRef.current,
        selectedKambiTickets: selectedKambiTicketsRef.current,
      })

      queryClient.invalidateQueries({ queryKey: queryKeys.betLoopSharePendingTickets })
      queryClient.invalidateQueries(betLoopPrincipleProfileQuery)
    },
    onMutate() {
      setIsFirstShareModalOpen(false)
      onDismiss()
    },
    onError: handleShareToBetLoopMutateError,
  })

  return { mutate, isPending }
}

// =============
// Local Helpers
// =============

function handleShareToBetLoopMutateError(error: Error) {
  trackShareBetError({
    message: error.message,
    destination: 'Bet Loop',
    location: 'bet-loop',
    betType: undefined,
  })

  addToast({
    id: 'bet-loop-quick-share--error',
    timeoutMs: 6_000,
    title: 'Oops, unable to share to Bet Loop',
    icon: 'error',
    theme: 'dark',
  })
}
